import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ExternalLink, Loader, Printer } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoints';
import { TENANT_PATHS } from '../../routing/paths.ts';
import {
  buildProcessReturnState,
  currentPathWithSearch,
  soxCycleEndToEndFlowchartPath,
  soxProcessFlowchartPath,
} from '../../routing/processNavigation.ts';
import { useProcessBackNavigation } from '../../hooks/useProcessBackNavigation.ts';
import CombinedProcessFlowchartViewer from './CombinedProcessFlowchartViewer';
import { fetchAllCycleProcesses } from './cycleEndToEndProcesses';
import { fetchCombinedFlowchartPages } from './fetchCombinedFlowcharts';
import { combinedVersionLabel } from './pickCombinedFlowchartVersion';
import './combinedFlowchartPrint.css';

export default function CycleCombinedFlowchartPage() {
  const { cycleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { goBack } = useProcessBackNavigation(
    cycleId ? soxCycleEndToEndFlowchartPath(cycleId) : TENANT_PATHS.SOX_DASHBOARD
  );

  const {
    data: processes = [],
    isLoading: isLoadingProcesses,
    isError: isProcessesError,
    isSuccess: isProcessesSuccess,
  } = useQuery({
    queryKey: ['cycle-end-to-end-flowchart', cycleId],
    enabled: Boolean(cycleId),
    queryFn: () => fetchAllCycleProcesses(axiosInstance, API_ENDPOINTS.getProcesses, cycleId),
  });

  const processListKey = useMemo(
    () =>
      processes
        .map((process) => String(process?.id ?? process?.process_id ?? ''))
        .filter(Boolean)
        .join(','),
    [processes]
  );

  const {
    data: pages = [],
    isLoading: isLoadingPages,
    isError: isPagesError,
  } = useQuery({
    queryKey: ['cycle-combined-flowchart', cycleId, processListKey],
    enabled: Boolean(cycleId) && isProcessesSuccess,
    queryFn: () => fetchCombinedFlowchartPages(cycleId, processes),
  });

  const isLoading = isLoadingProcesses || (isProcessesSuccess && isLoadingPages);
  const isError = isProcessesError || isPagesError;
  const pageCount = pages.length;

  const emptyMessage = useMemo(() => {
    if (isLoading || isError) return '';
    if (pageCount === 0) {
      return 'No process flowcharts are available to combine yet.';
    }
    return '';
  }, [isError, isLoading, pageCount]);

  useEffect(() => {
    document.body.classList.add('combined-flowchart-print-mode');
    const onAfterPrint = () => {
      document.body.classList.remove('combined-flowchart-print-preparing');
      window.dispatchEvent(new Event('combined-flowchart-fit-screen'));
    };
    window.addEventListener('afterprint', onAfterPrint);
    return () => {
      window.removeEventListener('afterprint', onAfterPrint);
      document.body.classList.remove('combined-flowchart-print-mode');
      document.body.classList.remove('combined-flowchart-print-preparing');
    };
  }, []);

  const handlePrint = () => {
    document.body.classList.add('combined-flowchart-print-preparing');
    window.setTimeout(() => {
      window.dispatchEvent(new Event('combined-flowchart-fit'));
      window.setTimeout(() => window.print(), 280);
    }, 80);
  };

  const openProcessFlowchart = (page) => {
    const processListKey = String(page?.process?.process_id ?? page?.processId ?? '').trim();
    if (!cycleId || !processListKey) return;
    const versionNumber = Number(page?.versionNumber ?? page?.version?.value);
    navigate(soxProcessFlowchartPath(cycleId, processListKey), {
      state: {
        ...buildProcessReturnState(currentPathWithSearch(location)),
        ...(Number.isFinite(versionNumber) ? { versionNumber } : {}),
      },
    });
  };

  return (
    <div className="combined-flowchart-print-root min-h-screen bg-gray-50">
      <div className="combined-flowchart-screen-only sticky top-0 z-10 border-b border-gray-200 bg-white px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-7xl items-center gap-4">
          <button
            type="button"
            onClick={goBack}
            className="rounded-full p-2 text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            aria-label="Back"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-semibold text-gray-900">Combined flowchart</h1>
            <p className="text-sm text-gray-500">Cycle: {String(cycleId || '').toUpperCase()}</p>
          </div>
          {pageCount > 0 ? (
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Printer size={16} />
              Print
            </button>
          ) : null}
        </div>
      </div>

      {isLoading ? (
        <div className="flex min-h-[60vh] items-center justify-center">
          <Loader className="h-8 w-8 animate-spin text-gray-900" />
        </div>
      ) : isError ? (
        <div className="mx-auto max-w-3xl px-4 py-8">
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Failed to load combined flowcharts for this cycle.
          </div>
        </div>
      ) : emptyMessage ? (
        <div className="mx-auto max-w-3xl px-4 py-8">
          <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center text-sm text-gray-500">
            {emptyMessage}
          </div>
        </div>
      ) : (
        <div className="combined-flowchart-print-pages mx-auto w-full max-w-7xl">
          {pages.map((page, index) => (
            <section
              key={String(page.processId)}
              className="combined-flowchart-print-page relative h-[calc(100vh-73px)] border-b border-gray-200 bg-white"
            >
              <div className="combined-flowchart-print-page-header absolute inset-x-0 top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 bg-white/95 px-4 py-3 sm:px-6">
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {page.process?.name || page.processId}
                  </p>
                  <p className="text-xs text-gray-500">
                    {page.process?.process_id || page.processId} · {combinedVersionLabel(page.version)}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => openProcessFlowchart(page)}
                    className="combined-flowchart-screen-only inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <ExternalLink size={13} />
                    Go to flowchart
                  </button>
                  <p className="text-xs font-medium text-gray-500">
                    Page {index + 1} of {pageCount}
                  </p>
                </div>
              </div>
              <div className="combined-flowchart-print-canvas absolute inset-x-0 bottom-0 top-[57px]">
                <CombinedProcessFlowchartViewer
                  flowchartData={page.flowchartData}
                  flowchartVersionId={page.flowchartVersionId}
                  processUuid={page.process?.id}
                />
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
